// Procedural low-poly soldier figures for the PlayCanvas renderer, drawn with hardware
// instancing so hundreds of models render in a handful of draw calls (GDD §8.2 perf
// budget). Each soldier is a small assembly of primitive parts (box legs, box/capsule
// torso, box-helm/sphere-head, pauldrons/horns, weapon). Legion is steel-and-crimson and
// blockier; Kindled is ash-and-flame and raggier with ember emissives.
//
// THE POSTURE PILLAR IS SACRED (GDD §7.1): each posture poses the part assembly so it
// reads instantly at gameplay zoom — advancing leans forward mid-stride, in-cover/pinned
// hunker progressively lower, prone flattens into a long ground-hugging slab, broken
// sprints upright with no weapon, frenzy raises the weapon with an ember flare, melee
// lunges with an oscillating weapon-up scrum pose. `poseFor` derives the params; the part
// transforms below turn them into per-part local matrices, instanced against the figure's
// world transform.

import * as pc from "playcanvas";
import type { Faction, Posture } from "../sim/types";

const DEG = 180 / Math.PI;
const FIG = 12; // unit-figure → world-unit scale (soldier ≈ 20 units tall)

// ---- canonical part indices (shared pose logic; meshes/materials differ per faction) --
const enum Part {
  LEG_L,
  LEG_R,
  TORSO,
  HEAD,
  SHOULDER,
  BACK,
  HORN_L,
  HORN_R,
  WEAPON,
  COUNT,
}

interface Pose {
  crouch: number; // 0 upright … 1 fully hunkered
  lean: number; // forward lean, radians
  stride: number; // leg-swing phase amplitude driver (0 = still)
  armUp: number; // weapon elevation 0..1
  bob: number; // vertical bob offset (unit space)
  prone: boolean;
  frenzy: boolean;
}

interface Kit {
  wl: number; // weapon length (unit space)
  blade: boolean; // melee weapon → angled up
  bulk: number; // torso/shoulder widen
  bigBack: boolean; // fuel tank / heavy pack
}

function kitFor(sil: string): Kit {
  switch (sil) {
    case "heavy":
      return { wl: 1.35, blade: false, bulk: 0.3, bigBack: false };
    case "assault":
      return { wl: 0.8, blade: true, bulk: 0.45, bigBack: true };
    case "zealot":
      return { wl: 0.7, blade: false, bulk: 0, bigBack: false };
    case "firebearer":
      return { wl: 0.85, blade: false, bulk: 0.25, bigBack: true };
    case "shock":
      return { wl: 0.9, blade: true, bulk: 0.4, bigBack: false };
    default:
      return { wl: 0.98, blade: false, bulk: 0, bigBack: false }; // line
  }
}

function poseFor(posture: Posture, phase: number): Pose {
  const s = Math.sin(phase * 10);
  switch (posture) {
    case "prone":
      return { crouch: 1, lean: 0, stride: 0, armUp: 0, bob: 0, prone: true, frenzy: false };
    case "pinned":
      return { crouch: 0.82, lean: 0.05, stride: 0, armUp: 0.28, bob: 0, prone: false, frenzy: false };
    case "incover":
      return { crouch: 0.6, lean: 0.08, stride: 0, armUp: 0.42, bob: 0, prone: false, frenzy: false };
    case "firing":
      return { crouch: 0.16, lean: 0.34 + s * 0.05, stride: 0, armUp: 0.5, bob: 0, prone: false, frenzy: false };
    case "advancing":
      return { crouch: 0.06, lean: 0.42, stride: 1, armUp: 0.22, bob: Math.abs(s) * 0.05, prone: false, frenzy: false };
    case "broken":
      return { crouch: 0, lean: 0.85, stride: 1.35, armUp: 0, bob: Math.abs(s) * 0.09, prone: false, frenzy: false };
    case "frenzied":
      return { crouch: 0, lean: 0.6, stride: 1.3, armUp: 0.9, bob: Math.abs(s) * 0.08, prone: false, frenzy: true };
    case "melee":
      return { crouch: 0.12, lean: 0.55 + Math.sin(phase * 9) * 0.28, stride: 0.2, armUp: 0.85 + Math.sin(phase * 9) * 0.1, bob: 0, prone: false, frenzy: false };
    default: // idle
      return { crouch: 0.08, lean: 0.04, stride: 0, armUp: 0.2, bob: 0, prone: false, frenzy: false };
  }
}

// ---- local part transforms -------------------------------------------------
// Scratch objects (no per-figure allocation).
const _p = new pc.Vec3();
const _s = new pc.Vec3();
const _q = new pc.Quat();

function trs(out: pc.Mat4, px: number, py: number, pz: number, ex: number, ey: number, ez: number, sx: number, sy: number, sz: number): void {
  _p.set(px, py, pz);
  _q.setFromEulerAngles(ex, ey, ez);
  _s.set(sx, sy, sz);
  out.setTRS(_p, _q, _s);
}

/** Fill `out[Part.*]` with the figure-local matrix of every canonical part (unit space). */
function fillLocals(out: pc.Mat4[], pose: Pose, phase: number, kit: Kit): void {
  const bulk = kit.bulk;
  if (pose.prone) {
    // Long, flat, ground-hugging slab read from above.
    trs(out[Part.LEG_L], -0.32, 0.11, -0.13, 0, 0, 0, 0.5, 0.14, 0.16);
    trs(out[Part.LEG_R], -0.32, 0.11, 0.13, 0, 0, 0, 0.5, 0.14, 0.16);
    trs(out[Part.TORSO], 0.06, 0.17, 0, 0, 0, 0, 0.74, 0.22, 0.5 + bulk * 0.2);
    trs(out[Part.HEAD], 0.56, 0.22, 0, 0, 0, 0, 0.3, 0.26, 0.3);
    trs(out[Part.SHOULDER], 0.06, 0.24, 0, 0, 0, 0, 0.34, 0.14, 0.58 + bulk * 0.2);
    trs(out[Part.BACK], -0.12, 0.28, 0, 0, 0, 0, 0.2, 0.16, 0.36);
    trs(out[Part.HORN_L], 0.66, 0.32, -0.12, 0, 0, -70, 0.11, 0.26, 0.11);
    trs(out[Part.HORN_R], 0.66, 0.32, 0.12, 0, 0, -70, 0.11, 0.26, 0.11);
    trs(out[Part.WEAPON], 0.72, 0.2, 0.16, 0, 0, 0, kit.wl, 0.08, 0.08);
    return;
  }

  const c = pose.crouch;
  const legLen = 0.62 * (1 - c * 0.5);
  const legCy = legLen / 2;
  const hipY = 0.6 - c * 0.28 - (0.62 - legLen); // ride down with bent legs
  const strideX = pose.stride * Math.sin(phase * 10) * 0.2;
  const bob = pose.bob;

  const torsoY = hipY + 0.4 * (1 - c * 0.18) + bob;
  const torsoH = 0.6 * (1 - c * 0.26);
  const headY = torsoY + torsoH * 0.5 + 0.24;
  const shoulderY = torsoY + torsoH * 0.28;
  const weaponY = torsoY + 0.04;
  const backY = torsoY;

  const leanDeg = pose.lean * DEG;
  const armAbove = torsoY - hipY;
  const leanX = Math.sin(pose.lean) * armAbove;

  // legs (stride swing in the forward axis)
  trs(out[Part.LEG_L], strideX, legCy, -0.15, 0, 0, pose.stride * Math.sin(phase * 10) * 22, 0.17, legLen, 0.18);
  trs(out[Part.LEG_R], -strideX, legCy, 0.15, 0, 0, -pose.stride * Math.sin(phase * 10) * 22, 0.17, legLen, 0.18);
  // torso (lean tips the top toward +x)
  trs(out[Part.TORSO], leanX * 0.4, torsoY, 0, 0, 0, -leanDeg, 0.34, torsoH, 0.46 + bulk * 0.34);
  // head / helm
  trs(out[Part.HEAD], leanX * 0.9, headY, 0, 0, 0, -leanDeg * 0.6, 0.3, 0.3, 0.3);
  // shoulders / pauldrons (wide + blocky, widened by bulk)
  trs(out[Part.SHOULDER], leanX * 0.6, shoulderY, 0, 0, 0, -leanDeg, 0.38, 0.2, 0.62 + bulk * 0.4);
  // back pack / fuel tank (ember-emissive for Kindled)
  const backS = kit.bigBack ? 0.56 : 0.4;
  trs(out[Part.BACK], -0.24 + leanX * 0.5, backY, 0, 0, 0, -leanDeg, 0.18, backS, kit.bigBack ? 0.46 : 0.38);
  // horns (Kindled) — splayed spikes up-and-out
  trs(out[Part.HORN_L], leanX * 0.9 + 0.02, headY + 0.2, -0.12, -12, 0, -leanDeg - 22, 0.12, 0.3, 0.12);
  trs(out[Part.HORN_R], leanX * 0.9 + 0.02, headY + 0.2, 0.12, 12, 0, -leanDeg + 22, 0.12, 0.3, 0.12);
  // weapon (from shoulder, +x; blades & frenzy/melee raise the tip)
  const up = kit.blade ? 40 + pose.armUp * 20 : -pose.armUp * 8 + (pose.frenzy ? 45 : 0);
  const wCx = 0.34 + kit.wl * 0.5;
  trs(out[Part.WEAPON], wCx + leanX * 0.6, weaponY, 0.2, 0, 0, up - leanDeg, kit.wl, 0.09, 0.09);
}

// ---- instanced batch per faction -------------------------------------------

interface Mat {
  diffuse: [number, number, number];
  emissive?: [number, number, number];
  emissiveI?: number;
}

const hex = (h: string): [number, number, number] => {
  const n = parseInt(h.slice(1), 16);
  return [((n >> 16) & 255) / 255, ((n >> 8) & 255) / 255, (n & 255) / 255];
};

const LEGION_MATS: Record<string, Mat> = {
  body: { diffuse: hex("#525b66") },
  steel: { diffuse: hex("#828d99") },
  trim: { diffuse: hex("#8a1f2b") },
  dark: { diffuse: hex("#26221e") },
  metal: { diffuse: hex("#1d1a17") },
};
const KINDLED_MATS: Record<string, Mat> = {
  body: { diffuse: hex("#3f382f") },
  steel: { diffuse: hex("#6a5d4f") },
  bone: { diffuse: hex("#d9c7a0") },
  dark: { diffuse: hex("#22201c") },
  metal: { diffuse: hex("#1a1714") },
  ember: { diffuse: hex("#3a2a1c"), emissive: hex("#ff7a1e"), emissiveI: 2.2 },
};

interface PartBatch {
  part: Part;
  mi: pc.MeshInstance;
  vb: pc.VertexBuffer;
  data: Float32Array;
  count: number;
}

const MAX_PER_FACTION = 420;

function makeMaterial(m: Mat): pc.StandardMaterial {
  const mat = new pc.StandardMaterial();
  mat.useMetalness = false;
  mat.diffuse.set(m.diffuse[0], m.diffuse[1], m.diffuse[2]);
  mat.gloss = 0.25;
  mat.specular.set(0.08, 0.08, 0.08);
  if (m.emissive) {
    mat.emissive.set(m.emissive[0], m.emissive[1], m.emissive[2]);
    mat.emissiveIntensity = m.emissiveI ?? 1;
  }
  mat.update();
  return mat;
}

class FactionFigures {
  private batches: PartBatch[] = [];
  private node = new pc.GraphNode();
  private locals: pc.Mat4[] = [];
  private world = new pc.Mat4();
  private tmp = new pc.Mat4();

  constructor(app: pc.AppBase, layer: pc.Layer, faction: Faction, castShadows: boolean) {
    const dev = app.graphicsDevice;
    const box = pc.Mesh.fromGeometry(dev, new pc.BoxGeometry({ widthSegments: 1, heightSegments: 1, lengthSegments: 1 }));
    const cap = pc.Mesh.fromGeometry(dev, new pc.CapsuleGeometry({ radius: 0.5, height: 1, sides: 8 }));
    const sph = pc.Mesh.fromGeometry(dev, new pc.SphereGeometry({ radius: 0.5, latitudeBands: 6, longitudeBands: 8 }));
    const cone = pc.Mesh.fromGeometry(dev, new pc.ConeGeometry({ baseRadius: 0.5, peakRadius: 0, height: 1, capSegments: 6 }));
    for (let i = 0; i < Part.COUNT; i++) this.locals.push(new pc.Mat4());

    const P = faction === "legion" ? LEGION_MATS : KINDLED_MATS;
    const defs: Array<[Part, pc.Mesh, Mat]> =
      faction === "legion"
        ? [
            [Part.LEG_L, box, P.dark],
            [Part.LEG_R, box, P.dark],
            [Part.TORSO, box, P.body],
            [Part.HEAD, box, P.steel],
            [Part.SHOULDER, box, P.trim],
            [Part.WEAPON, box, P.metal],
          ]
        : [
            [Part.LEG_L, box, P.dark],
            [Part.LEG_R, box, P.dark],
            [Part.TORSO, cap, P.body],
            [Part.HEAD, sph, P.steel],
            [Part.HORN_L, cone, P.bone],
            [Part.HORN_R, cone, P.bone],
            [Part.BACK, box, P.ember],
            [Part.WEAPON, box, P.metal],
          ];

    const aabb = new pc.BoundingBox(new pc.Vec3(1200, 60, 800), new pc.Vec3(1600, 260, 1200));
    for (const [part, mesh, matDef] of defs) {
      const mat = makeMaterial(matDef);
      const mi = new pc.MeshInstance(mesh, mat, this.node);
      mi.cull = false;
      mi.castShadow = castShadows;
      mi.setCustomAabb(aabb);
      const format = pc.VertexFormat.getDefaultInstancingFormat(dev);
      const data = new Float32Array(MAX_PER_FACTION * 16);
      // Upload zeroed initial data so the GPU vertex buffer exists before the first
      // render, then start at 0 instances until draw() populates it (avoids a WebGL
      // "no buffer bound to enabled attribute" on the opening frames).
      const vb = new pc.VertexBuffer(dev, format, MAX_PER_FACTION, { usage: pc.BUFFER_DYNAMIC, data: data.buffer as ArrayBuffer });
      mi.setInstancing(vb);
      if (mi.instancingData) mi.instancingData.count = 0;
      layer.addMeshInstances([mi]);
      this.batches.push({ part, mi, vb, data, count: 0 });
    }
  }

  begin(): void {
    for (const b of this.batches) b.count = 0;
  }

  add(px: number, pz: number, facing: number, posture: Posture, phase: number, sil: string): void {
    const kit = kitFor(sil);
    const pose = poseFor(posture, phase);
    fillLocals(this.locals, pose, phase, kit);
    // figure world = T(pos) · Ry(-facing) · S(FIG)
    _p.set(px, 0, pz);
    _q.setFromEulerAngles(0, -facing * DEG, 0);
    _s.set(FIG, FIG, FIG);
    this.world.setTRS(_p, _q, _s);
    for (const b of this.batches) {
      if (b.count >= MAX_PER_FACTION) continue;
      this.tmp.mul2(this.world, this.locals[b.part]);
      b.data.set(this.tmp.data, b.count * 16);
      b.count++;
    }
  }

  end(): void {
    for (const b of this.batches) {
      b.vb.setData(b.data.buffer as ArrayBuffer);
      if (b.mi.instancingData) b.mi.instancingData.count = b.count;
    }
  }

  destroy(): void {
    for (const b of this.batches) {
      b.mi.destroy();
    }
    this.batches = [];
  }
}

export class SoldierBatches {
  private legion: FactionFigures;
  private kindled: FactionFigures;

  constructor(app: pc.AppBase, layer: pc.Layer, castShadows: boolean) {
    this.legion = new FactionFigures(app, layer, "legion", castShadows);
    this.kindled = new FactionFigures(app, layer, "kindled", castShadows);
  }

  begin(): void {
    this.legion.begin();
    this.kindled.begin();
  }

  add(faction: Faction, sil: string, px: number, pz: number, facing: number, posture: Posture, phase: number): void {
    (faction === "legion" ? this.legion : this.kindled).add(px, pz, facing, posture, phase, sil);
  }

  end(): void {
    this.legion.end();
    this.kindled.end();
  }

  destroy(): void {
    this.legion.destroy();
    this.kindled.destroy();
  }
}
